import { useState } from 'react';
import adminClient from '../api/adminClient';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const useAdminApi = <T = any>() => {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<ApiError | null>();
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = async (url: string, options: RequestOptions = {}) => {
    setError(null);
    setLoading(true);

    try {
      const { method = 'GET', headers = {}, body } = options;

      let response;
      if (method === 'GET') {
        response = await adminClient.get(url, { headers });
      } else if (method === 'POST') {
        response = await adminClient.post(url, body, { headers });
      } else if (method === 'PUT') {
        response = await adminClient.put(url, body, { headers });
      } else if (method === 'PATCH') {
        response = await adminClient.patch(url, body, { headers });
      } else if (method === 'DELETE') {
        response = await adminClient.delete(url, { headers });
      } else {
        throw new Error(`Unsupported method: ${method}`);
      }

      setData(response.data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        setError(
          new ApiError(
            error.response.data?.message ||
              `HTTP Error ${error.response.status}`,
            error.response.status,
            error.response.data,
          ),
        );
      } else {
        setError(new ApiError(error.message || 'Network Error', 0, error));
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getAllProducts = async () =>
    await fetchData('/product', { method: 'GET' });
  const getProductsByCategory = async (category: string) =>
    await fetchData(`/product?category=${category}`, { method: 'GET' });
  const createProduct = async (product: any) =>
    await fetchData('/product', { method: 'POST', body: product });
  const deleteProductById = async (id: string) =>
    await fetchData(`/product/${id}`, { method: 'DELETE' });
  const updateProduct = async (id: string, body: any) =>
    await fetchData(`/product/${id}`, { method: 'PATCH', body });
  const getProductsById = async (id: string) =>
    await fetchData(`/product/${id}`, { method: 'GET' });

  const getOrders = async (
    type: 'consultation' | 'order',
    status: 'active' | 'completed' | 'canceled',
  ) => {
    const urlParams = new URLSearchParams();
    urlParams.append('type', type.toUpperCase());
    urlParams.append('status', status.toUpperCase());
    return await fetchData(`/order?${urlParams.toString()}`, { method: 'GET' });
  };

  const changeOrderStatus = async (
    id: string,
    status: 'active' | 'canceled' | 'completed',
  ) =>
    await fetchData(`/order/${id}`, {
      method: 'PUT',
      body: { status: status.toUpperCase() },
    });

  const deleteOrder = async (id: string) =>
    await fetchData(`/order/${id}`, { method: 'DELETE' });

  const deleteAllCanceledOrders = async (
    type: 'consultation' | 'order' = 'consultation',
  ) => {
    const urlParams = new URLSearchParams();
    urlParams.append('type', type.toUpperCase());
    return await fetchData(`/order/canceled/all?${urlParams.toString()}`, {
      method: 'DELETE',
    });
  };
  const getReviews = async (
    page: number = 1,
    type?: 'organization' | 'product',
    sortEntity?: 'username' | 'rating' | 'created_at',
    sortOrder?: 'desc' | 'asc' | null,
    searchWord?: string,
    limit: number = 3,
  ) => {
    const urlParams = new URLSearchParams();
    urlParams.append('limit', limit.toString());
    urlParams.append('page', page.toString());
    if (sortOrder) urlParams.append('order', sortOrder);
    if (sortEntity) urlParams.append('sortBy', sortEntity);
    if (searchWord) urlParams.append('search', searchWord);
    if (type) urlParams.append('type', type);
    return await fetchData(`/reviews?${urlParams.toString()}`, {
      method: 'GET',
    });
  };

  const removeReview = async (id: string) =>
    await fetchData(`/reviews/${id}`, { method: 'DELETE' });
  const getReviewsByType = async (param: 'product' | 'organization') =>
    await fetchData(`/reviews/${param}`, { method: 'GET' });
  const getProductReview = async (id: string) =>
    await fetchData(`/reviews/product/${id}`, { method: 'GET' });

  const getPopularProducts = async (popularitySortOrder: 'desc' | 'asc') => {
    const urlParams = new URLSearchParams();
    urlParams.append('sortBy', 'popularityScore');
    urlParams.append('order', popularitySortOrder);
    urlParams.append('limit', '40');
    return await fetchData(`/product?${urlParams.toString()}`, {
      method: 'GET',
    });
  };

  const getRecentProducts = async (sortOrder: 'desc' | 'asc', limit = 40) => {
    const urlParams = new URLSearchParams();
    urlParams.append('sortBy', 'created_at');
    urlParams.append('order', sortOrder);
    urlParams.append('limit', `${limit}`);
    return await fetchData(`/product?${urlParams.toString()}`, {
      method: 'GET',
    });
  };

  return {
    data,
    error,
    loading,
    fetchData,
    getAllProducts,
    getProductsByCategory,
    createProduct,
    deleteProductById,
    updateProduct,
    getProductsById,
    getOrders,
    changeOrderStatus,
    deleteOrder,
    deleteAllCanceledOrders,
    getReviews,
    removeReview,
    getReviewsByType,
    getProductReview,
    getPopularProducts,
    getRecentProducts,
  };
};

export default useAdminApi;
